import React from 'react';
import { motion } from 'motion/react';
import { Instagram as InstaIcon, Camera, Sparkles, Hash } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const Instagram = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Instagram Caption Generator - Boost Your Feed"
        description="Create viral Instagram captions, find relevant hashtags, and get content ideas for Reels and Carousels with PlataPost's AI tools."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-pink-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Camera className="w-3.5 h-3.5" />
              <span>Visual Focused</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI Instagram <br /> <span className="text-pink-500">Caption Generator.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Dominate the feed with captions that stop the scroll. PlataPost generates engaging copy, Reels hooks, and hashtag sets tailored for IG success.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Grow My Instagram
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 flex items-center justify-center border border-white/5 relative overflow-hidden group shadow-2xl shadow-pink-500/5">
             <div className="absolute inset-0 bg-pink-500/5 blur-[100px] pointer-events-none" />
             <div className="w-64 h-[450px] bg-white/5 rounded-[2.5rem] shadow-2xl border border-white/10 overflow-hidden flex flex-col relative z-10 group-hover:scale-105 transition-transform duration-700">
                <div className="h-12 bg-white/5 border-b border-white/5 flex items-center px-6">
                  <div className="w-3 h-3 rounded-full bg-pink-500 mr-3 shadow-[0_0_8px_rgba(236,72,153,0.5)]" />
                  <div className="w-20 h-2 bg-white/10 rounded-full" />
                </div>
                <div className="flex-1 bg-gradient-to-b from-white/[0.02] to-transparent" />
                <div className="h-32 p-6 space-y-3 bg-white/5 border-t border-white/5">
                  <div className="h-2 w-full bg-pink-500/20 rounded-full animate-pulse" />
                  <div className="h-2 w-3/4 bg-pink-500/10 rounded-full" />
                  <div className="h-2 w-1/2 bg-indigo-500/20 rounded-full" />
                </div>
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-24 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Platform Specific Strategy</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Master the Instagram algorithm with AI-optimized content.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
             {[
               { icon: Hash, title: "Smart Hashtags", desc: "Generate 30 relevant, low-competition hashtags to boost search visibility." },
               { icon: Camera, title: "Reels & Carousels", desc: "Get visual concept storyboards and hook ideas for short-form video." },
               { icon: Sparkles, title: "Emoji Mastery", desc: "AI-placed emojis that fit the Instagram aesthetic perfectly." }
             ].map((feature, i) => (
                <div key={i} className="p-12 glass-card rounded-[3rem] border border-white/5 hover:bg-white/5 transition-all group">
                   <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-10 text-pink-400 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl shadow-pink-500/5">
                      <feature.icon className="w-10 h-10" />
                   </div>
                   <h3 className="text-3xl font-black text-white mb-6 tracking-tight">{feature.title}</h3>
                   <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
                </div>
             ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-pink-500/10">
           <div className="absolute inset-0 bg-pink-500/10 blur-[150px] pointer-events-none" />
           <h2 className="text-4xl lg:text-6xl font-black mb-10 text-white tracking-tighter leading-tight relative z-10">Ready to Level Up?</h2>
           <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto font-medium relative z-10 leading-relaxed">
             Stop guessing and start growing. Our AI-driven Instagram tools are your shortcut to a professional feed.
           </p>
           <Link to="/booking" className="relative z-10 inline-flex px-12 py-6 bg-pink-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-pink-700 transition-all shadow-2xl shadow-pink-600/30">
              Get Demo
           </Link>
        </div>
      </div>
    </div>
  );
};

export default Instagram;
